import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  ArrowLeft,
  Check,
  X,
  Minus,
  Trophy,
  Hash,
  ChevronRight,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  getMemberBracketView,
  matchCodeToFrontId,
  type MemberBracketView,
} from "@/lib/bracket.functions";
import { listMyEntries } from "@/lib/entries.functions";
import {
  getCompareLanding,
  type CompareLandingRow,
} from "@/lib/compare.functions";
import { useLeague } from "@/lib/league-context";
import { Flag } from "@/components/Flag";
import { cn } from "@/lib/utils";

const compareSearchSchema = z.object({
  a: z.string().uuid().optional(),
  b: z.string().uuid().optional(),
});

export const Route = createFileRoute("/_app/$league/compare")({
  component: ComparePage,
  validateSearch: compareSearchSchema,
  head: () => ({
    meta: [
      { title: "Compare brackets — FIFA 2026" },
      {
        name: "description",
        content: "Side-by-side comparison of two players' bracket picks.",
      },
    ],
  }),
});

// Knockout rounds in display order, matching DB match_code prefixes.
const KO_ROUNDS: { key: string; label: string }[] = [
  { key: "R32", label: "Round of 32" },
  { key: "R16", label: "Round of 16" },
  { key: "QF", label: "Quarterfinals" },
  { key: "SF", label: "Semifinals" },
  { key: "F", label: "Final" },
  { key: "3P", label: "3rd place" },
];

function roundForCode(code: string): string {
  // DB canonical match codes: R32-1, R16-1, QF-1, SF-1, FINAL, 3RD
  if (code === "FINAL") return "F";
  if (code === "3RD") return "3P";
  for (const r of KO_ROUNDS) {
    if (code.startsWith(r.key + "-")) return r.key;
  }
  return "?";
}



function ComparePage() {
  const { league: leagueSlug } = Route.useParams();
  const { a, b } = Route.useSearch();
  const leagueCtx = useLeague();
  const navigate = useNavigate();

  const listMineFn = useAuthedServerFn(listMyEntries);
  const [myEntries, setMyEntries] = useState<
    { id: string; bracketName: string }[]
  >([]);
  const [myEntriesReady, setMyEntriesReady] = useState(false);

  // Load my entries once we have the league id (so we can default `a` and
  // power the multi-entry dropdown).
  useEffect(() => {
    if (!leagueCtx?.id) return;
    let cancelled = false;
    listMineFn({ data: { leagueId: leagueCtx.id } })
      .then((r) => {
        if (cancelled) return;
        setMyEntries(
          r.entries.map((e) => ({ id: e.id, bracketName: e.bracketName })),
        );
      })
      .catch(() => {
        if (!cancelled) setMyEntries([]);
      })
      .finally(() => {
        if (!cancelled) setMyEntriesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueCtx?.id, listMineFn]);

  // Auto-fill `a` with viewer's first own entry when missing.
  useEffect(() => {
    if (!myEntriesReady) return;
    if (a) return;
    if (myEntries.length === 0) return;
    void navigate({
      to: "/$league/compare",
      params: { league: leagueSlug },
      search: { a: myEntries[0].id, b },
      replace: true,
    });
  }, [myEntriesReady, a, b, myEntries, navigate, leagueSlug]);

  if (!leagueCtx) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  const lockAt = leagueCtx.bracketLockAt;
  const isLocked = !!lockAt && new Date(lockAt) <= new Date();

  if (!isLocked) {
    return (
      <PreLockNotice leagueSlug={leagueSlug} bracketLockAt={lockAt} />
    );
  }

  // Participant with zero entries: nothing to compare AS — but they can still
  // view others' brackets via the leaderboard.
  if (myEntriesReady && myEntries.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold">Compare brackets</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You don&apos;t have a bracket in this league, so there&apos;s nothing
          to compare from. Head to the leaderboard to view other players&apos;
          picks.
        </p>
        <Link
          to="/$league"
          params={{ league: leagueSlug }}
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to leaderboard
        </Link>
      </div>
    );
  }

  // While we're auto-filling `a`, render a spinner instead of flashing the
  // landing table with no viewer entry.
  if (!a) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (b) {
    return (
      <FocusedView
        leagueSlug={leagueSlug}
        leagueId={leagueCtx.id}
        tournamentType={leagueCtx.tournamentType}
        viewerEntryId={a}
        peerEntryId={b}
      />
    );
  }

  return (
    <LandingView
      leagueSlug={leagueSlug}
      leagueId={leagueCtx.id}
      viewerEntryId={a}
      myEntries={myEntries}
    />
  );
}

// =================================================================
// Pre-lock notice
// =================================================================

function PreLockNotice({
  leagueSlug,
  bracketLockAt,
}: {
  leagueSlug: string;
  bracketLockAt: string | null;
}) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-12 text-center">
      <h1 className="font-display text-2xl font-bold">Compare brackets</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Comparison unlocks for everyone once brackets lock
        {bracketLockAt
          ? ` at ${new Date(bracketLockAt).toLocaleString()}`
          : ""}
        .
      </p>
      <Link
        to="/$league"
        params={{ league: leagueSlug }}
        className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to leaderboard
      </Link>
    </div>
  );
}

// =================================================================
// Landing — sortable table of peers vs viewer
// =================================================================

type SortKey = "overlap" | "owner" | "bracket";

function LandingView({
  leagueSlug,
  leagueId,
  viewerEntryId,
  myEntries,
}: {
  leagueSlug: string;
  leagueId: string;
  viewerEntryId: string;
  myEntries: { id: string; bracketName: string }[];
}) {
  const fn = useAuthedServerFn(getCompareLanding);
  const navigate = useNavigate();
  const [rows, setRows] = useState<CompareLandingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "overlap",
    dir: "desc",
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn({ data: { leagueId, viewerEntryId } })
      .then((r) => {
        if (!cancelled) setRows(r.rows);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fn, leagueId, viewerEntryId]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((x, y) => {
      let cmp = 0;
      if (sort.key === "overlap") cmp = x.overlapPct - y.overlapPct;
      else if (sort.key === "owner")
        cmp = x.ownerDisplayName.localeCompare(y.ownerDisplayName);
      else if (sort.key === "bracket")
        cmp = x.bracketName.localeCompare(y.bracketName);
      return sort.dir === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [rows, sort]);

  function toggleSort(k: SortKey) {
    setSort((s) =>
      s.key === k
        ? { key: k, dir: s.dir === "desc" ? "asc" : "desc" }
        : { key: k, dir: k === "overlap" ? "desc" : "asc" },
    );
  }

  function entryLabel(r: CompareLandingRow): string {
    if (r.entryType === "for_someone_else" && r.proxyDisplayName) {
      return r.relationship
        ? `${r.proxyDisplayName} (${r.relationship})`
        : r.proxyDisplayName;
    }
    return r.ownerDisplayName;
  }

  function goToFocus(peerId: string) {
    void navigate({
      to: "/$league/compare",
      params: { league: leagueSlug },
      search: { a: viewerEntryId, b: peerId },
    });
  }

  return (
    <div className="mx-auto max-w-4xl px-3 py-4 sm:px-6 sm:py-8">
      <div className="mb-4">
        <Link
          to="/$league"
          params={{ league: leagueSlug }}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to league
        </Link>
      </div>

      <header className="mb-5">
        <h1 className="font-display text-2xl font-bold sm:text-3xl">
          Compare brackets
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a bracket below to put it next to yours.
        </p>
      </header>

      {myEntries.length > 1 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2">
          <label
            htmlFor="comparing-as"
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Comparing as
          </label>
          <select
            id="comparing-as"
            value={viewerEntryId}
            onChange={(e) =>
              navigate({
                to: "/$league/compare",
                params: { league: leagueSlug },
                search: { a: e.target.value },
                replace: true,
              })
            }
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            {myEntries.map((m) => (
              <option key={m.id} value={m.id}>
                {m.bracketName}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" /> Loading
          comparisons…
        </div>
      )}

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {!loading && !error && sortedRows.length === 0 && (
        <p className="rounded-md border border-border bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
          No other brackets to compare against yet.
        </p>
      )}

      {!loading && !error && sortedRows.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-border sm:block">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <SortableTh
                    label="Owner"
                    active={sort.key === "owner"}
                    dir={sort.dir}
                    onClick={() => toggleSort("owner")}
                  />
                  <SortableTh
                    label="Bracket"
                    active={sort.key === "bracket"}
                    dir={sort.dir}
                    onClick={() => toggleSort("bracket")}
                  />
                  <SortableTh
                    label="% Overlap"
                    active={sort.key === "overlap"}
                    dir={sort.dir}
                    onClick={() => toggleSort("overlap")}
                    align="right"
                  />
                  <th className="px-3 py-2 text-right">Matches</th>
                  <th className="w-16 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => (
                  <tr
                    key={r.entryId}
                    className={cn(
                      "border-t border-border hover:bg-secondary/30",
                      idx === 0 && "border-t-0",
                    )}
                  >
                    <td className="px-3 py-2">{entryLabel(r)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.bracketName}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {r.overlapPct}%
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground tabular-nums">
                      {r.matchesShared}/{r.matchesCompared}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => goToFocus(r.entryId)}
                        className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-background px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10"
                      >
                        View <ChevronRight className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 sm:hidden">
            {sortedRows.map((r) => (
              <button
                key={r.entryId}
                type="button"
                onClick={() => goToFocus(r.entryId)}
                className="block w-full rounded-lg border border-border bg-card/40 px-3 py-3 text-left hover:border-primary/40"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0 flex-1 truncate font-semibold">
                    {entryLabel(r)}
                  </div>
                  <div className="text-lg font-bold text-primary tabular-nums">
                    {r.overlapPct}%
                  </div>
                </div>
                <div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                  <div className="min-w-0 flex-1 truncate">
                    {r.bracketName}
                  </div>
                  <div className="tabular-nums">
                    {r.matchesShared}/{r.matchesCompared} matches
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
  align,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {label}
        {active && <span aria-hidden>{dir === "desc" ? "▾" : "▴"}</span>}
      </button>
    </th>
  );
}

// =================================================================
// Focused 2-bracket view — 3 tabs: Mine / Theirs / Differences
// =================================================================

function FocusedView({
  leagueSlug,
  leagueId,
  tournamentType,
  viewerEntryId,
  peerEntryId,
}: {
  leagueSlug: string;
  leagueId: string;
  tournamentType: string;
  viewerEntryId: string;
  peerEntryId: string;
}) {
  const fn = useAuthedServerFn(getMemberBracketView);
  const [aData, setAData] = useState<MemberBracketView | null>(null);
  const [bData, setBData] = useState<MemberBracketView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fn({ data: { leagueId, targetEntryId: viewerEntryId } }),
      fn({ data: { leagueId, targetEntryId: peerEntryId } }),
    ])
      .then(([ra, rb]) => {
        if (cancelled) return;
        setAData(ra);
        setBData(rb);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(
            e instanceof Error ? e.message : "Failed to load comparison",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, viewerEntryId, peerEntryId, fn]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Loading comparison…
      </div>
    );
  }

  if (error || !aData || !bData) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="text-sm text-destructive">{error ?? "Could not load."}</p>
        <Link
          to="/$league/compare"
          params={{ league: leagueSlug }}
          search={{ a: viewerEntryId }}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to compare
        </Link>
      </div>
    );
  }

  const teamById = new Map(aData.teams.map((t) => [t.id, t]));
  for (const t of bData.teams) if (!teamById.has(t.id)) teamById.set(t.id, t);

  const aPicksByCode = new Map(aData.picks.map((p) => [p.match_code, p]));
  const bPicksByCode = new Map(bData.picks.map((p) => [p.match_code, p]));

  // Collect all knockout match codes (DB canonical) in stable order
  const codeSet = new Set<string>();
  for (const p of aData.picks) codeSet.add(p.match_code);
  for (const p of bData.picks) codeSet.add(p.match_code);
  for (const m of aData.matches) codeSet.add(m.match_code);

  const sortedCodes = Array.from(codeSet).sort((x, y) => {
    const rx = KO_ROUNDS.findIndex((r) => r.key === roundForCode(x));
    const ry = KO_ROUNDS.findIndex((r) => r.key === roundForCode(y));
    if (rx !== ry) return rx - ry;
    const nx = parseInt(x.split("-")[1] ?? "0", 10);
    const ny = parseInt(y.split("-")[1] ?? "0", 10);
    return nx - ny;
  });

  // Overlap headline — combine knockout team picks + group position picks +
  // bonus picks + tiebreaker. Each category contributes to shared/compared
  // only when BOTH sides have a value for that slot.
  let koShared = 0;
  let koCompared = 0;
  let divergent = 0;
  let goalsShared = 0;
  let goalsCompared = 0;
  for (const code of sortedCodes) {
    const ap = aPicksByCode.get(code);
    const bp = bPicksByCode.get(code);
    if (ap?.picked_team_id && bp?.picked_team_id) {
      koCompared += 1;
      if (ap.picked_team_id === bp.picked_team_id) koShared += 1;
      else divergent += 1;
    }
    if (ap?.predicted_total_goals != null && bp?.predicted_total_goals != null) {
      goalsCompared += 1;
      if (ap.predicted_total_goals === bp.predicted_total_goals) goalsShared += 1;
    }
  }

  // Group codes by knockout round
  const codesByRound = new Map<string, string[]>();
  for (const code of sortedCodes) {
    const r = roundForCode(code);
    if (!codesByRound.has(r)) codesByRound.set(r, []);
    codesByRound.get(r)!.push(code);
  }

  // Group picks (G+K leagues) — group blocks + per-entry position picks
  const hasGroups =
    tournamentType === "fifa_wc_mens_group_knockout" &&
    aData.groups.length > 0;

  function renderTeamCell(teamId: string | null | undefined) {
    if (!teamId) return <span className="text-xs text-muted-foreground">—</span>;
    const t = teamById.get(teamId);
    if (!t) return <span className="text-xs text-muted-foreground">?</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        <Flag code={t.code} size={14} />
        <span className="text-sm font-medium">{t.name}</span>
      </span>
    );
  }

  // Row tint helper: green when both picked the same team, red when both
  // picked but diverged, neutral when one/both missing.
  function rowTint(aTeam: string | null, bTeam: string | null): string {
    if (!aTeam || !bTeam) return "";
    return aTeam === bTeam ? "bg-success/10" : "bg-destructive/10";
  }

  // Build a map of bonus picks per side for easy lookup.
  const aBonusByQ = new Map(
    aData.bonusPicks.map((p) => [p.league_bonus_question_id, p.pick_value]),
  );
  const bBonusByQ = new Map(
    bData.bonusPicks.map((p) => [p.league_bonus_question_id, p.pick_value]),
  );
  function bonusLabel(
    pick: string | null,
    q: MemberBracketView["bonusQuestions"][number],
  ): string {
    if (!pick) return "—";
    if (pick === "over") return q.over_label ?? "Over";
    if (pick === "under") return q.under_label ?? "Under";
    return pick;
  }

  const aTb = aData.championTotalKnockoutGoals;
  const bTb = bData.championTotalKnockoutGoals;
  const tbDelta = aTb != null && bTb != null ? Math.abs(aTb - bTb) : null;

  // Group position picks — count per (group, position) slot where both filled.
  let groupShared = 0;
  let groupCompared = 0;
  if (hasGroups) {
    for (const g of aData.groups) {
      const myPos = aData.groupPicks[g.id] ?? {};
      const theirPos = bData.groupPicks[g.id] ?? {};
      for (const pos of [1, 2, 3, 4]) {
        const at = myPos[pos];
        const bt = theirPos[pos];
        if (at && bt) {
          groupCompared += 1;
          if (at === bt) groupShared += 1;
        }
      }
    }
  }

  // Bonus picks — count per question where both answered.
  let bonusShared = 0;
  let bonusCompared = 0;
  for (const q of aData.bonusQuestions) {
    const ap = aBonusByQ.get(q.id);
    const bp = bBonusByQ.get(q.id);
    if (ap && bp) {
      bonusCompared += 1;
      if (ap === bp) bonusShared += 1;
    }
  }

  // Tiebreaker — exact match only, counts as 1 slot when both filled.
  const tbCompared = aTb != null && bTb != null ? 1 : 0;
  const tbShared = tbCompared === 1 && aTb === bTb ? 1 : 0;

  const totalShared = koShared + goalsShared + groupShared + bonusShared + tbShared;
  const totalCompared =
    koCompared + goalsCompared + groupCompared + bonusCompared + tbCompared;
  const overlapPct =
    totalCompared > 0 ? Math.round((totalShared / totalCompared) * 100) : 0;

  type Cat = { label: string; shared: number; compared: number };
  const categories: Cat[] = [];
  if (hasGroups)
    categories.push({ label: "Group", shared: groupShared, compared: groupCompared });
  categories.push({ label: "Knockout winners", shared: koShared, compared: koCompared });
  categories.push({ label: "Knockout goal totals", shared: goalsShared, compared: goalsCompared });
  if (aData.bonusQuestions.length > 0)
    categories.push({ label: "Bonus", shared: bonusShared, compared: bonusCompared });
  categories.push({
    label: "Tiebreaker",
    shared: tbShared,
    compared: tbCompared,
  });


  return (
    <div className="mx-auto max-w-5xl px-3 py-4 sm:px-6 sm:py-8">
      <div className="mb-4 flex items-center justify-between gap-2">
        <Link
          to="/$league/compare"
          params={{ league: leagueSlug }}
          search={{ a: viewerEntryId }}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to compare list
        </Link>
      </div>

      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold sm:text-3xl">
          Bracket comparison
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {totalShared} shared · {divergent} different in knockout ·{" "}
          <span className="font-semibold text-foreground">{overlapPct}%</span> overlap
          {totalCompared > 0 && (
            <span className="text-muted-foreground"> ({totalCompared} compared)</span>
          )}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card/40 px-2 py-0.5 text-[11px] text-muted-foreground"
            >
              <span className="font-semibold text-foreground">{c.label}</span>
              <span className="tabular-nums">
                {c.shared}/{c.compared}
              </span>
            </span>
          ))}
        </div>
      </header>

      <div className="mb-6 grid grid-cols-2 gap-3 rounded-lg border border-border bg-card/40 p-3">
        <PlayerSummary data={aData} side="left" leagueSlug={leagueSlug} />
        <PlayerSummary data={bData} side="right" leagueSlug={leagueSlug} />
      </div>

      {/* Groups section — only G+K leagues */}
      {hasGroups && (
        <section className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Group stage picks
          </h2>
          <div className="space-y-4">
            {aData.groups.map((g) => {
              const myPos = aData.groupPicks[g.id] ?? {};
              const theirPos = bData.groupPicks[g.id] ?? {};
              const positions = [1, 2, 3, 4];
              return (
                <div
                  key={g.id}
                  className="overflow-hidden rounded-lg border border-border"
                >
                  <div className="bg-secondary/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Group {g.groupCode}
                  </div>
                  {positions.map((pos) => {
                    const aTeam = myPos[pos] ?? null;
                    const bTeam = theirPos[pos] ?? null;
                    return (
                      <div
                        key={`${g.id}-${pos}`}
                        className={cn(
                          "grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-t border-border px-3 py-2 text-sm first:border-t-0",
                          rowTint(aTeam, bTeam),
                        )}
                      >
                        <div className="min-w-0 truncate text-right">
                          {renderTeamCell(aTeam)}
                        </div>
                        <div className="px-1 text-[10px] font-mono text-muted-foreground">
                          #{pos}
                        </div>
                        <div className="min-w-0 truncate text-left">
                          {renderTeamCell(bTeam)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Knockout section */}
      <section className="mb-6 space-y-6">
        <div className="sticky top-0 z-10 -mx-3 mb-4 border-y border-border bg-background/95 px-3 py-4 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 rounded-full bg-primary" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                Knockout Picks
              </h2>
              <p className="text-xs text-muted-foreground">
                Round-by-round bracket choices
              </p>
            </div>
          </div>
        </div>
        {KO_ROUNDS.map((r) => {
          const codes = codesByRound.get(r.key);
          if (!codes || codes.length === 0) return null;
          return (
            <div key={r.key}>
              <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {r.label}
              </h3>
              <div className="overflow-hidden rounded-lg border border-border">
                {codes.map((code, idx) => {
                  const ap = aPicksByCode.get(code);
                  const bp = bPicksByCode.get(code);
                  const aTeam = ap?.picked_team_id ?? null;
                  const bTeam = bp?.picked_team_id ?? null;
                  const same = !!aTeam && !!bTeam && aTeam === bTeam;
                  const diff = !!aTeam && !!bTeam && aTeam !== bTeam;
                  const both = !!aTeam && !!bTeam;
                  return (
                    <div
                      key={code}
                      className={cn(
                        "grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2 text-sm",
                        idx > 0 && "border-t border-border",
                        rowTint(aTeam, bTeam),
                      )}
                    >
                      <div className="min-w-0 text-right">
                        {renderTeamCell(aTeam)}
                        {ap?.predicted_total_goals != null && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                            {ap.predicted_total_goals} goals
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-center gap-0.5 px-1 text-[10px] text-muted-foreground">
                        <span className="font-mono">{matchCodeToFrontId(code)}</span>
                        {same ? (
                          <Check className="h-3.5 w-3.5 text-success" />
                        ) : diff ? (
                          <X className="h-3.5 w-3.5 text-destructive" />
                        ) : (
                          <Minus className="h-3.5 w-3.5 text-muted-foreground/50" />
                        )}
                        {!both && (
                          <span className="text-[9px] text-muted-foreground">
                            no pick
                          </span>
                        )}
                        {ap?.predicted_total_goals != null &&
                          bp?.predicted_total_goals != null && (
                            <span
                              className={cn(
                                "text-[9px] font-semibold",
                                ap.predicted_total_goals === bp.predicted_total_goals
                                  ? "text-success"
                                  : "text-muted-foreground/70",
                              )}
                            >
                              {ap.predicted_total_goals === bp.predicted_total_goals
                                ? "goals ✓"
                                : "goals ✗"}
                            </span>
                          )}
                      </div>
                      <div className="min-w-0 text-left">
                        {renderTeamCell(bTeam)}
                        {bp?.predicted_total_goals != null && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                            {bp.predicted_total_goals} goals
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Bonus picks */}
      {aData.bonusQuestions.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Bonus picks
          </h2>
          <div className="overflow-hidden rounded-lg border border-border">
            {aData.bonusQuestions.map((q, idx) => {
              const aPick = aBonusByQ.get(q.id) ?? null;
              const bPick = bBonusByQ.get(q.id) ?? null;
              const both = !!aPick && !!bPick;
              const same = both && aPick === bPick;
              const tint = !both
                ? ""
                : same
                  ? "bg-success/10"
                  : "bg-destructive/10";
              return (
                <div
                  key={q.id}
                  className={cn(
                    "px-3 py-2",
                    idx > 0 && "border-t border-border",
                    tint,
                  )}
                >
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {q.short_label ?? q.question_label}
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
                    <div
                      className={cn(
                        "truncate text-right font-medium",
                        !aPick && "italic text-muted-foreground",
                      )}
                    >
                      {bonusLabel(aPick, q)}
                    </div>
                    <div className="px-1 text-muted-foreground">
                      {!both ? (
                        <Minus className="h-3.5 w-3.5 text-muted-foreground/50" />
                      ) : same ? (
                        <Check className="h-3.5 w-3.5 text-success" />
                      ) : (
                        <X className="h-3.5 w-3.5 text-destructive" />
                      )}
                    </div>
                    <div
                      className={cn(
                        "truncate text-left font-medium",
                        !bPick && "italic text-muted-foreground",
                      )}
                    >
                      {bonusLabel(bPick, q)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Tiebreaker */}
      <section>
        <div className="rounded-lg border border-border bg-card/40 p-4">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Hash className="h-3.5 w-3.5" /> Tiebreaker — champion knockout goals
          </h3>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center">
            <div className="rounded-md bg-secondary/40 px-3 py-2">
              <div className="font-display text-2xl font-bold tabular-nums">
                {aTb ?? "—"}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {aData.target.displayName}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {tbDelta != null ? (
                <>
                  Δ <span className="font-semibold text-foreground tabular-nums">{tbDelta}</span>
                </>
              ) : (
                "—"
              )}
            </div>
            <div className="rounded-md bg-secondary/40 px-3 py-2">
              <div className="font-display text-2xl font-bold tabular-nums">
                {bTb ?? "—"}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {bData.target.displayName}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


function PlayerSummary({
  data,
  side,
  leagueSlug,
}: {
  data: MemberBracketView;
  side: "left" | "right";
  leagueSlug: string;
}) {
  return (
    <div className={cn("min-w-0", side === "right" && "text-right")}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {data.rank != null ? `Rank #${data.rank}` : "Unranked"}
      </div>
      <Link
        to="/$league/members/$entryId"
        params={{ league: leagueSlug, entryId: data.target.entryId }}
        className="block truncate font-display text-lg font-bold text-foreground hover:text-primary"
      >
        {data.target.displayName}
      </Link>
      {data.target.secondaryLabel && (
        <div className="truncate text-xs text-muted-foreground">
          {data.target.secondaryLabel}
        </div>
      )}
      <div
        className={cn(
          "mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary",
          side === "right" && "flex-row-reverse",
        )}
      >
        <Trophy className="h-3.5 w-3.5" />
        <span>
          {data.bracketScore.bracketPoints}{" "}
          <span className="text-xs font-normal text-muted-foreground">pts</span>
        </span>
      </div>
    </div>
  );
}

